import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { reduceProject } from "../../common/project";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  portfolios: [],
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
};

function handleRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleSuccess(
  state: State,
  { portfolios }: Boundaries["retrieveProjectPortfolios"]["success"],
): State {
  return { portfolios, request: setRequestStateToSuccess() };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleCreateProjectPortfolioRequest(
  state: State,
  { portfolio }: Boundaries["createProjectPortfolio"]["request"],
): State {
  return {
    ...state,
    portfolios: [{ ...portfolio, uuid: portfolio.name }, ...state.portfolios],
  };
}

function handleCreateProjectPortfolioSuccess(
  state: State,
  { portfolio }: Boundaries["createProjectPortfolio"]["success"],
): State {
  return {
    ...state,
    portfolios: [
      portfolio,
      ...state.portfolios.filter(({ uuid }) => uuid !== portfolio.name),
    ],
  };
}

function handleCreateProjectPortfolioFailure(
  state: State,
  { portfolio }: Boundaries["createProjectPortfolio"]["failure"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.filter(({ uuid }) => uuid !== portfolio.name),
  };
}

function handleAssignProjectToPortfolioRequest(
  state: State,
  { portfolio, project }: Boundaries["assignProjectToPortfolio"]["request"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((p) =>
      p.uuid === portfolio.uuid
        ? { ...portfolio, projects: [...p.projects, project] }
        : p,
    ),
  };
}

function handleAssignProjectToPortfolioSuccess(
  state: State,
  { portfolio, project }: Boundaries["assignProjectToPortfolio"]["success"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((p) => {
      const index = p.projects.findIndex((prj) => prj.uuid === project.uuid);

      return p.uuid === portfolio.uuid
        ? {
            ...portfolio,
            projects:
              index !== -1
                ? [
                    ...p.projects.slice(0, index),
                    project,
                    ...p.projects.slice(index + 1),
                  ]
                : [...p.projects, project],
          }
        : p;
    }),
  };
}

function handleAssignProjectToPortfolioFailure(
  state: State,
  { portfolio, project }: Boundaries["assignProjectToPortfolio"]["failure"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((p) =>
      p.uuid === portfolio.uuid
        ? {
            ...portfolio,
            projects: p.projects.filter(({ uuid }) => uuid !== project.uuid),
          }
        : p,
    ),
  };
}

function handleRemoveProjectPortfolioRequest(
  state: State,
  { portfolio }: Boundaries["removeProjectPortfolio"]["request"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.filter(({ uuid }) => uuid !== portfolio.uuid),
  };
}

function handleRemoveProjectPortfolioSuccess(
  state: State,
  { portfolio }: Boundaries["removeProjectPortfolio"]["success"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.filter(({ uuid }) => uuid !== portfolio.uuid),
  };
}

function handleRemoveProjectPortfolioFailure(
  state: State,
  { portfolio }: Boundaries["removeProjectPortfolio"]["failure"],
): State {
  return {
    ...state,
    portfolios: [portfolio, ...state.portfolios],
  };
}

function handleEditProjectPortfolioRequest(
  state: State,
  { portfolio, nextPortfolio }: Boundaries["editProjectPortfolio"]["request"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((p) =>
      p.uuid === portfolio.uuid ? { ...portfolio, ...nextPortfolio } : p,
    ),
  };
}

function handleEditProjectPortfolioSuccess(
  state: State,
  { portfolio }: Boundaries["editProjectPortfolio"]["success"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((p) =>
      p.uuid === portfolio.uuid ? portfolio : p,
    ),
  };
}

function handleEditProjectPortfolioFailure(
  state: State,
  { portfolio }: Boundaries["editProjectPortfolio"]["failure"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((p) =>
      p.uuid === portfolio.uuid ? portfolio : p,
    ),
  };
}

function handleRemoveProjectFromPortfolioRequest(
  state: State,
  { portfolio, project }: Boundaries["removeProjectFromPortfolio"]["request"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((p) =>
      p.uuid === portfolio.uuid
        ? {
            ...portfolio,
            projects: p.projects.filter(({ uuid }) => uuid !== project.uuid),
          }
        : p,
    ),
  };
}

function handleRemoveProjectFromPortfolioSuccess(
  state: State,
  { portfolio, project }: Boundaries["removeProjectFromPortfolio"]["success"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((p) =>
      p.uuid === portfolio.uuid
        ? {
            ...portfolio,
            projects: p.projects.filter(({ uuid }) => uuid !== project.uuid),
          }
        : p,
    ),
  };
}

function handleRemoveProjectFromPortfolioFailure(
  state: State,
  { portfolio, project }: Boundaries["removeProjectFromPortfolio"]["failure"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((p) =>
      p.uuid === portfolio.uuid
        ? {
            ...portfolio,
            projects: [...p.projects, project],
          }
        : p,
    ),
  };
}

function handleRemoveProject(
  state: State,
  project: Boundaries["removeProject"]["success"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((portfolio) => ({
      ...portfolio,
      projects: portfolio.projects.filter(({ uuid }) => uuid !== project.uuid),
    })),
  };
}

function handleCreateProject(
  state: State,
  { project, portfolios }: Boundaries["createProject"]["success"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((portfolio) =>
      portfolios.some(({ uuid }) => uuid === portfolio.uuid)
        ? { ...portfolio, projects: [...portfolio.projects, project] }
        : portfolio,
    ),
  };
}

function handleCreateProjectFromTemplate(
  state: State,
  { project }: Boundaries["useProjectModel"]["success"],
): State {
  return {
    ...state,
    portfolios: state.portfolios.map((portfolio) =>
      project.portfolios.some(({ uuid }) => uuid === portfolio.uuid)
        ? { ...portfolio, projects: [...portfolio.projects, project] }
        : portfolio,
    ),
  };
}

function propagateEvent(state = initialState, event: Event): State {
  return {
    ...state,
    portfolios: state.portfolios.map((portfolio) => ({
      ...portfolio,
      projects: portfolio.projects.map((project) =>
        reduceProject(project, event),
      ),
    })),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveProjectPortfoliosRequested]: handleRequest,
      [events.retrieveProjectPortfoliosSucceeded]: handleSuccess,
      [events.retrieveProjectPortfoliosFailed]: handleFailure,
      [events.createProjectPortfolioRequested]:
        handleCreateProjectPortfolioRequest,
      [events.createProjectPortfolioSucceeded]:
        handleCreateProjectPortfolioSuccess,
      [events.createProjectPortfolioFailed]:
        handleCreateProjectPortfolioFailure,
      [events.assignProjectToPortfolioRequested]:
        handleAssignProjectToPortfolioRequest,
      [events.assignProjectToPortfolioSucceeded]:
        handleAssignProjectToPortfolioSuccess,
      [events.assignProjectToPortfolioFailed]:
        handleAssignProjectToPortfolioFailure,
      [events.removeProjectPortfolioRequested]:
        handleRemoveProjectPortfolioRequest,
      [events.removeProjectPortfolioSucceeded]:
        handleRemoveProjectPortfolioSuccess,
      [events.removeProjectPortfolioFailed]:
        handleRemoveProjectPortfolioFailure,
      [events.editProjectPortfolioRequested]: handleEditProjectPortfolioRequest,
      [events.editProjectPortfolioSucceeded]: handleEditProjectPortfolioSuccess,
      [events.editProjectPortfolioFailed]: handleEditProjectPortfolioFailure,
      [events.removeProjectFromPortfolioRequested]:
        handleRemoveProjectFromPortfolioRequest,
      [events.removeProjectFromPortfolioSucceeded]:
        handleRemoveProjectFromPortfolioSuccess,
      [events.removeProjectFromPortfolioFailed]:
        handleRemoveProjectFromPortfolioFailure,
      [events.removeProjectSucceeded]: handleRemoveProject,
      [events.createProjectSucceeded]: handleCreateProject,
      [events.useProjectModelSucceeded]: handleCreateProjectFromTemplate,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
